import { useState } from 'react';
import { AnimatedNumber } from '../AnimatedNumber';
import { ExploreCard } from './ExploreCard';
import { TokenRail } from './TokenRail';
import { ChecklistGlyph } from './icons';
import {
  EXPLORE_FILTERS,
  filterExploreCoins,
  type ExploreFilter,
} from './exploreModel';
import type { ArtRenderer, CoinView } from './model';
import type { QueueMotion } from './queueMotion';
import type { RailMotion } from './railMotion';
import { recentCallOrder, railCoins } from './arenaLists';

function ExploreTile({
  coin,
  now,
  art,
  index,
}: {
  coin: CoinView;
  now: number;
  art: ArtRenderer;
  index: number;
}) {
  // Freeze the entrance slot: live insertions and price ticks must not rewind it.
  // A newly loaded page gets its own short cascade instead of an ever-growing wait.
  const [delay] = useState(() => 100 + (index % 12) * 50);
  return (
    <li style={{ animationDelay: delay + 'ms' }}>
      <ExploreCard coin={coin} now={now} art={art} />
    </li>
  );
}

export function ExploreScreen({
  coins,
  sidebarCoins = coins,
  now,
  ready,
  art,
  filter,
  onFilter,
  onArena,
  catchingUp = false,
  motion,
  railMotion,
}: {
  coins: CoinView[];
  sidebarCoins?: CoinView[];
  now: number;
  ready: boolean;
  art: ArtRenderer;
  filter: ExploreFilter;
  onFilter: (filter: ExploreFilter) => void;
  onArena: () => void;
  catchingUp?: boolean;
  motion?: QueueMotion;
  railMotion?: RailMotion;
}) {
  const [page, setPage] = useState({ filter, limit: 12 });
  const limit = page.filter === filter ? page.limit : 12;
  const filtered = filterExploreCoins(coins, filter, now);
  const shown = filtered.slice(0, limit);
  const chosenIds = sidebarCoins
    .filter((coin) => coin.choice)
    .map((coin) => coin.id);
  const [previousCalls, setPreviousCalls] = useState(chosenIds);
  const callOrder = recentCallOrder(previousCalls, chosenIds);
  if (
    callOrder.length !== previousCalls.length ||
    callOrder.some((id, i) => id !== previousCalls[i])
  )
    setPreviousCalls(callOrder);
  const { mine } = railCoins(sidebarCoins, now, callOrder);
  const empty =
    filter === 'My bets'
      ? [
          'Your first call starts in Arena.',
          'Your active calls and confirmed results will appear here.',
        ]
      : filter === 'Settled'
        ? [
            'No settled rounds yet.',
            'Only verified results appear in this list.',
          ]
        : [
            'No rounds to watch right now.',
            'Tokens arrive here when their prediction window closes.',
          ];
  return (
    <section className="explore-screen" aria-labelledby="explore-title">
      <h1 id="explore-title">Explore</h1>
      <div className="explore-board">
        <nav className="explore-filters" aria-label="Filter Explore">
          {EXPLORE_FILTERS.map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={filter === item}
              onClick={() => {
                setPage({ filter: item, limit: 12 });
                onFilter(item);
              }}
            >
              {item}
            </button>
          ))}
        </nav>
        {catchingUp && (
          <p className="explore-note" role="status">
            Catching up on recent graduations. Rounds will update automatically.
          </p>
        )}
        {!ready && !shown.length ? (
          <div className="explore-empty" role="status">
            <h2>Loading Explore…</h2>
            <p>Fetching rounds and your saved calls.</p>
          </div>
        ) : !shown.length ? (
          <div className="explore-empty">
            <h2>{empty[0]}</h2>
            <p>{empty[1]}</p>
            <button type="button" onClick={onArena}>
              Go to Arena
            </button>
          </div>
        ) : (
          <>
            <ul className="explore-grid" aria-label={filter + ' rounds'}>
              {shown.map((coin, index) => (
                <ExploreTile
                  key={coin.id}
                  coin={coin}
                  now={now}
                  art={art}
                  index={index}
                />
              ))}
            </ul>
            {shown.length < filtered.length && (
              <button
                type="button"
                className="explore-more"
                onClick={() => setPage({ filter, limit: limit + 12 })}
              >
                Show more{' '}
                <span>
                  (<AnimatedNumber value={filtered.length - shown.length} />)
                </span>
              </button>
            )}
          </>
        )}
      </div>
      <div className="explore-rail">
        <TokenRail
          side="left"
          label="Your calls"
          coins={mine}
          now={now}
          art={art}
          motion={railMotion}
          queueMotion={motion}
          action={{
            label: 'Show my bets',
            glyph: <ChecklistGlyph />,
            onClick: () => {
              setPage({ filter: 'My bets', limit: 12 });
              onFilter('My bets');
            },
          }}
        />
      </div>
    </section>
  );
}
